// src/app/features/user/userService.js
import api, { getConfig, setToken, clearAuth } from "../../../utils/axiosConfig";

const safeErrorMessage = (error, fallback = "Request failed") => {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.response?.data ||
    error?.message ||
    fallback
  );
};

/* =========================
   AUTH
========================= */
const register = async (userData) => {
  try {
    const response = await api.post(`user/register`, userData, {
      headers: { "Content-Type": "application/json" },
      withCredentials: true,
    });

    if (response.data) {
      localStorage.setItem("customer", JSON.stringify(response.data));

      const t = response.data?.token || response.data?.accessToken;
      if (t) setToken(t);

      return response.data;
    }
    return null;
  } catch (error) {
    console.error("Error during registration:", error);
    throw new Error(safeErrorMessage(error, "Registration failed"));
  }
};

const login = async (userData) => {
  try {
    const response = await api.post(`user/login`, userData, {
      headers: { "Content-Type": "application/json" },
      withCredentials: true,
    });

    if (response.data) {
      localStorage.setItem("customer", JSON.stringify(response.data));

      const t = response.data?.token || response.data?.accessToken;
      if (t) setToken(t);

      return response.data;
    }
    return null;
  } catch (error) {
    console.error("Error during login:", error);
    throw new Error(safeErrorMessage(error, "Login failed"));
  }
};

const googleLogin = async (payload) => {
  try {
    const response = await api.post(`user/google`, payload, {
      headers: { "Content-Type": "application/json" },
      withCredentials: true,
    });

    if (response.data) {
      const hasSessionToken =
        response.data?.token || response.data?.accessToken;

      if (hasSessionToken) {
        localStorage.setItem("customer", JSON.stringify(response.data));
        setToken(hasSessionToken);
      }

      return response.data;
    }

    return null;
  } catch (error) {
    throw new Error(safeErrorMessage(error, "Google login failed"));
  }
};

const completeGoogleProfile = async (payload) => {
  try {
    const response = await api.post(`user/google/complete-profile`, payload, {
      headers: { "Content-Type": "application/json" },
      withCredentials: true,
    });

    if (response.data) {
      localStorage.setItem("customer", JSON.stringify(response.data));

      const t = response.data?.token || response.data?.accessToken;
      if (t) setToken(t);

      return response.data;
    }

    return null;
  } catch (error) {
    throw new Error(
      safeErrorMessage(error, "Failed to complete Google profile")
    );
  }
};

const identify = async (payload) => {
  try {
    const response = await api.post(`user/identify`, payload, {
      headers: { "Content-Type": "application/json" },
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    throw new Error(safeErrorMessage(error, "Failed to verify identity"));
  }
};

const refresh = async () => {
  try {
    const response = await api.post(`user/refresh`, {}, { withCredentials: true });
    const newToken = response.data?.token || response.data?.accessToken;
    if (newToken) setToken(newToken);
    return response.data;
  } catch (error) {
    throw new Error(safeErrorMessage(error, "Failed to refresh token"));
  }
};

const logout = async () => {
  try {
    const response = await api.post(`user/logout`, {}, { withCredentials: true });
    clearAuth();
    return response?.data || { success: true };
  } catch (error) {
    clearAuth();
    throw new Error(safeErrorMessage(error, "Logout failed"));
  }
};

/* =========================
   EMAIL VERIFICATION
========================= */
const sendVerificationCode = async (payload) => {
  try {
    const response = await api.post(`user/send-verification-code`, payload, {
      headers: { "Content-Type": "application/json" },
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    throw new Error(safeErrorMessage(error, "Failed to send verification code"));
  }
};

const verifyCode = async (payload) => {
  try {
    const response = await api.post(`user/verify-code`, payload, {
      headers: { "Content-Type": "application/json" },
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    throw new Error(safeErrorMessage(error, "Failed to verify code"));
  }
};

/* =========================
   PROFILE
========================= */
const updateUser = async (data) => {
  try {
    const response = await api.put(`user/edit-user`, data, getConfig({}, true));
    return response.data;
  } catch (error) {
    throw new Error(safeErrorMessage(error, "Failed to update user"));
  }
};

/* =========================
   PASSWORD RESET
========================= */
const forgotPassCode = async (data) => {
  try {
    const response = await api.post(`user/forgot-password-code`, data, {
      headers: { "Content-Type": "application/json" },
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    throw new Error(safeErrorMessage(error, "Failed to send reset code"));
  }
};

const verifyResetCode = async (data) => {
  try {
    const response = await api.post(
      `user/verify-reset-code`,
      { email: data.email, code: data.code },
      { headers: { "Content-Type": "application/json" }, withCredentials: true }
    );
    return response.data;
  } catch (error) {
    throw new Error(safeErrorMessage(error, "Failed to verify reset code"));
  }
};

const resetPassCode = async (data) => {
  try {
    const response = await api.put(
      `user/reset-password-code`,
      { email: data.email, code: data.code, password: data.password },
      { headers: { "Content-Type": "application/json" }, withCredentials: true }
    );
    return response.data;
  } catch (error) {
    throw new Error(safeErrorMessage(error, "Failed to reset password"));
  }
};

/* =========================
   WISHLIST
========================= */
const getUserWishlist = async () => {
  try {
    const response = await api.get(`user/wishlist`, getConfig({}, true));
    return response.data;
  } catch (error) {
    throw new Error(safeErrorMessage(error, "Failed to fetch wishlist"));
  }
};

const removeProductFromWishlist = async (prodId) => {
  try {
    const response = await api.delete(`user/wishlist/${prodId}`, getConfig({}, true));
    return response.data;
  } catch (error) {
    throw new Error(safeErrorMessage(error, "Failed to remove from wishlist"));
  }
};

/* =========================
   CART
========================= */
const addToCart = async (cartData) => {
  try {
    const response = await api.post(`user/cart`, cartData, getConfig({}, true));
    return response.data;
  } catch (error) {
    throw new Error(safeErrorMessage(error, "Failed to add to cart"));
  }
};

const getCart = async () => {
  try {
    const response = await api.get(`user/cart`, getConfig({}, true));
    return response.data;
  } catch (error) {
    throw new Error(safeErrorMessage(error, "Failed to fetch cart"));
  }
};

const removeProductFromCart = async (cartItemId) => {
  try {
    const response = await api.delete(
      `user/delete-product-cart/${cartItemId}`,
      getConfig({}, true)
    );
    return response.data;
  } catch (error) {
    throw new Error(safeErrorMessage(error, "Failed to remove cart item"));
  }
};

const updateProductFromCart = async (cartDetail) => {
  try {
    const response = await api.put(
      `user/update-product-cart/${cartDetail.cartItemId}/${cartDetail.quantity}`,
      {},
      getConfig({}, true)
    );
    return response.data;
  } catch (error) {
    throw new Error(safeErrorMessage(error, "Failed to update cart quantity"));
  }
};

const emptyCart = async () => {
  try {
    const response = await api.delete(`user/empty-cart`, getConfig({}, true));
    return response.data;
  } catch (error) {
    throw new Error(safeErrorMessage(error, "Failed to empty cart"));
  }
};

/* =========================
   PAYMENTS + ORDERS
========================= */
const checkoutOrder = async (data) => {
  try {
    const response = await api.post(`user/order/checkout`, data, getConfig({}, true));
    return response.data;
  } catch (error) {
    throw new Error(safeErrorMessage(error, "Checkout failed"));
  }
};

const paymentVerification = async (data) => {
  try {
    const response = await api.post(
      `user/order/paymentVerification`,
      data,
      getConfig({}, true)
    );
    return response.data;
  } catch (error) {
    throw new Error(safeErrorMessage(error, "Payment verification failed"));
  }
};

const createOrder = async (data) => {
  try {
    const response = await api.post(
      `user/cart/create-order`,
      data,
      getConfig({}, true)
    );
    return response.data;
  } catch (error) {
    throw new Error(safeErrorMessage(error, "Failed to create order"));
  }
};

/* =========================
   DPO PAYMENT
========================= */
const createDpoToken = async (orderId) => {
  try {
    const response = await api.post(
      `dpo/create-token`,
      { orderId },
      getConfig({}, true)
    );
    return response.data;
  } catch (error) {
    throw new Error(safeErrorMessage(error, "Failed to start DPO payment"));
  }
};

const verifyDpoToken = async ({ token, orderId }) => {
  try {
    const response = await api.post(
      `dpo/verify-token`,
      { token, orderId },
      getConfig({}, true)
    );
    return response.data;
  } catch (error) {
    throw new Error(safeErrorMessage(error, "Failed to verify DPO payment"));
  }
};

const getUserOrders = async () => {
  try {
    const response = await api.get(`user/getmyorders`, getConfig({}, true));
    return response.data;
  } catch (error) {
    throw new Error(safeErrorMessage(error, "Failed to fetch orders"));
  }
};

const cancelOrder = async (orderId) => {
  try {
    const response = await api.put(
      `user/orders/${orderId}/cancel`,
      {},
      getConfig({}, true)
    );
    return response.data;
  } catch (error) {
    throw new Error(safeErrorMessage(error, "Failed to cancel order"));
  }
};

export const authService = {
  register,
  login,
  googleLogin,
  completeGoogleProfile,
  refresh,
  logout,

  identify,
  sendVerificationCode,
  verifyCode,

  updateUser,

  forgotPassCode,
  verifyResetCode,
  resetPassCode,

  getUserWishlist,
  removeProductFromWishlist,

  addToCart,
  getCart,
  removeProductFromCart,
  updateProductFromCart,
  emptyCart,

  checkoutOrder,
  paymentVerification,
  createOrder,

  createDpoToken,
  verifyDpoToken,

  getUserOrders,
  cancelOrder,
};

export default authService;